/**
 * 端末証明書(R7e)の選択肢と表示名。
 *
 * **真実はサーバー側**(`server/app/idp/device_certs.py`)。ここは見せ方だけを持つ。
 * 状態の値は英語(`issued` …)で来るので、**日本語にするのはここ 1 か所**。
 */
import type {
  DeviceCertEventAction,
  DeviceCertLang,
  DeviceCertMail,
  DeviceCertMailLangs,
  DeviceCertOs,
  DeviceCertStatus,
} from "../../lib/api";

export const DEVICE_CERT_OSES: DeviceCertOs[] = ["macos", "windows", "ios", "android", "other"];

export const DEVICE_CERT_OS_LABEL: Record<DeviceCertOs, string> = {
  macos: "macOS",
  windows: "Windows",
  ios: "iOS",
  android: "Android",
  other: "その他",
};

export const DEVICE_CERT_OS_OPTIONS = DEVICE_CERT_OSES.map((os) => ({
  value: os,
  label: DEVICE_CERT_OS_LABEL[os],
}));

export const DEVICE_CERT_STATUSES: DeviceCertStatus[] = [
  "active",
  "issued",
  "downloaded",
  "revoked",
  "expired",
];

/** 画面の言葉。`active` は「1 度でも判定で使われた」= 使用中。 */
export const DEVICE_CERT_STATUS_LABEL: Record<DeviceCertStatus, string> = {
  issued: "未ダウンロード",
  downloaded: "ダウンロード済み",
  active: "使用中",
  revoked: "失効",
  expired: "期限切れ",
};

export const DEVICE_CERT_STATUS_OPTIONS = DEVICE_CERT_STATUSES.map((s) => ({
  value: s,
  label: DEVICE_CERT_STATUS_LABEL[s],
}));

export function deviceCertStatusColor(status: DeviceCertStatus): string {
  if (status === "active") return "green";
  if (status === "downloaded") return "blue";
  if (status === "revoked") return "red";
  if (status === "expired") return "orange";
  return "default"; // issued(まだ落としていない)
}

export const DEVICE_CERT_LANGS: DeviceCertLang[] = ["ja", "en"];

export const DEVICE_CERT_LANG_LABEL: Record<DeviceCertLang, string> = {
  ja: "日本語",
  en: "English",
};

export const DEVICE_CERT_LANG_OPTIONS = DEVICE_CERT_LANGS.map((l) => ({
  value: l,
  label: DEVICE_CERT_LANG_LABEL[l],
}));

/** 操作履歴の action。サーバーの語をそのまま日本語にする。 */
export const DEVICE_CERT_ACTION_LABEL: Record<DeviceCertEventAction, string> = {
  issue: "発行",
  download: "ダウンロード",
  mail: "メール送信",
  revoke: "失効",
  note: "メモの変更",
  expire: "期限切れ",
  request_replace: "入替リクエスト",
};

export function deviceCertActionLabel(action: string): string {
  return DEVICE_CERT_ACTION_LABEL[action as DeviceCertEventAction] ?? action;
}

/** 一覧・小表の列(画面の言葉を 1 か所にまとめる)。 */
export const DEVICE_CERT_COLUMN_LABEL = {
  username: "ユーザー名",
  status: "状態",
  os: "OS",
  note: "備考",
  mailSubjectNote: "メール件名メモ",
  lastUsed: "最終使用",
  notAfter: "有効期限",
  actions: "操作",
} as const;

/** 有効期限が近いと見なす日数(KPI の `expiring_30d` と同じ)。 */
export const DEVICE_CERT_EXPIRING_DAYS = 30;

/**
 * メール文面に使える差し込み。**この 4 つだけ**(画面にも同じ一覧を出す)。
 * パスワードはメールに入れない(発行画面でだけ見せる)ので、差し込みも用意しない。
 */
export const DEVICE_CERT_MAIL_PLACEHOLDERS = [
  { key: "{username}", label: "ユーザー名" },
  { key: "{download_url}", label: "ダウンロード URL" },
  { key: "{expires_at}", label: "リンクの有効期限" },
  { key: "{subject_note}", label: "メール件名メモ" },
] as const;

const EMPTY_LANGS = (): DeviceCertMailLangs => ({
  ja: { subject: "", body: "" },
  en: { subject: "", body: "" },
});

/**
 * ドメイン設定の `device_cert_mail` を**必ず OS 5 × 言語 2 の形に揃える**。
 *
 * 設定がまだ入っていない realm では鍵ごと無い。そのまま PUT すると
 * 「入れたつもりの言語だけ消える」ので、読み込んだ時に穴を空文字で埋めてから
 * フォームに載せる(保存は常に全部そろった形で送る)。
 */
/** 1 通でも中身が入っているか(空のまま保存してサーバー側の既定を消さないための番人)。 */
export function deviceCertMailFilled(v: Partial<DeviceCertMail> | undefined): boolean {
  if (!v) return false;
  return DEVICE_CERT_OSES.some((os) =>
    DEVICE_CERT_LANGS.some((lang) => {
      const t = v[os]?.[lang];
      return !!(t?.subject || t?.body);
    }),
  );
}

export function fillDeviceCertMail(v: Partial<DeviceCertMail> | undefined): DeviceCertMail {
  const out = {} as DeviceCertMail;
  for (const os of DEVICE_CERT_OSES) {
    const langs = v?.[os];
    out[os] = EMPTY_LANGS();
    for (const lang of DEVICE_CERT_LANGS) {
      const t = langs?.[lang];
      out[os][lang] = { subject: t?.subject ?? "", body: t?.body ?? "" };
    }
  }
  return out;
}
